import { escapeHtml } from '../lib/escape';
import {
  ASPECT_RATIOS,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_IMAGE_SIZE,
  IMAGE_SIZES,
  areaDims,
} from '../lib/area-geometry';
import { areaColor } from '../lib/area-colors';
import { isChoice, isPhoto, notifyContent, notifyStructure, state, subscribeStructure } from '../state';
import type { Area, AreaImage, AreaTarget, AspectRatio, ImageSize } from '../types';

interface Elements {
  container: HTMLElement;
  onChange?: () => void;
}

let areaSeq = 0;
function nextAreaId(): string {
  return `area-${Date.now().toString(36)}-${(areaSeq++).toString(36)}`;
}

let areaTargetSeq = 0;
function nextAreaTargetId(): string {
  return `area-target-${Date.now().toString(36)}-${(areaTargetSeq++).toString(36)}`;
}

let areaImageSeq = 0;
function nextAreaImageId(): string {
  return `area-img-${Date.now().toString(36)}-${(areaImageSeq++).toString(36)}`;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });
}

export function mountAreasSection(opts: Elements): void {
  const { container, onChange } = opts;
  const expandedIds = new Set<string>();

  const emit = (): void => {
    onChange?.();
  };

  const render = (): void => {
    container.classList.add('areas-section');

    // Drop stale ids for deleted areas.
    for (const id of Array.from(expandedIds)) {
      if (!state.areas.some((a) => a.id === id)) expandedIds.delete(id);
    }

    const cropHtml = renderCropRow();

    const listHtml = state.areas.length === 0
      ? `<p class="areas-empty">No areas yet. Add one to define an edit region on the base image.</p>`
      : state.areas
          .map((a, idx) => renderAreaRow(a, idx, expandedIds.has(a.id)))
          .join('');

    container.innerHTML = `
      <div class="areas-header">
        <span class="areas-title">Areas</span>
        <span class="areas-count">${state.areas.length}</span>
        <button type="button" class="areas-add-btn" data-action="add">+ Add area</button>
      </div>
      <div class="areas-list" id="areas-list">${cropHtml}${listHtml}</div>
    `;
  };

  const onClick = (e: Event): void => {
    if (!(e.target instanceof Element)) return;

    const actionEl = e.target.closest<HTMLElement>('[data-action]');
    if (!actionEl) return;
    const action = actionEl.dataset.action;
    const row = actionEl.closest<HTMLElement>('.area-row');
    const areaId = row?.dataset.areaId;

    if (action === 'add') {
      const baseW = state.baseImage?.width ?? 1024;
      const baseH = state.baseImage?.height ?? 1024;
      const newArea: Area = {
        id: nextAreaId(),
        x: Math.max(0, Math.round((baseW - 512) / 2)),
        y: Math.max(0, Math.round((baseH - 512) / 2)),
        aspectRatio: DEFAULT_ASPECT_RATIO,
        imageSize: DEFAULT_IMAGE_SIZE,
        includeBase: true,
        includePhoto: false,
        hidden: false,
        targets: [],
        extraImages: [],
      };
      state.areas.push(newArea);
      notifyStructure();
      emit();
      return;
    }

    if (action === 'delete' && areaId) {
      state.areas = state.areas.filter((a) => a.id !== areaId);
      notifyStructure();
      emit();
      return;
    }

    if (action === 'toggle-expand' && areaId) {
      if (expandedIds.has(areaId)) expandedIds.delete(areaId);
      else expandedIds.add(areaId);
      row!.classList.toggle('expanded');
      return;
    }

    if (action === 'toggle-photo' && areaId) {
      const area = state.areas.find((a) => a.id === areaId);
      if (!area) return;
      area.includePhoto = !area.includePhoto;
      render();
      notifyContent();
      emit();
      return;
    }

    if (action === 'toggle-base' && areaId) {
      const area = state.areas.find((a) => a.id === areaId);
      if (!area) return;
      area.includeBase = !area.includeBase;
      render();
      notifyContent();
      emit();
      return;
    }

    if (action === 'toggle-hidden' && areaId) {
      const area = state.areas.find((a) => a.id === areaId);
      if (!area) return;
      area.hidden = !area.hidden;
      render();
      notifyStructure();
      emit();
      return;
    }

    if (action === 'toggle-crop-hidden') {
      if (!state.cropArea) return;
      state.cropArea.hidden = !state.cropArea.hidden;
      render();
      notifyStructure();
      emit();
      return;
    }

    if (action === 'aspect-popover' && areaId) {
      togglePopover(actionEl, () => aspectPopoverHtml(areaId), (chosen) => {
        const area = state.areas.find((a) => a.id === areaId);
        if (!area) return;
        area.aspectRatio = chosen as AspectRatio;
        render();
        notifyContent();
        emit();
      });
      return;
    }

    if (action === 'size-popover' && areaId) {
      togglePopover(actionEl, () => sizePopoverHtml(areaId), (chosen) => {
        const area = state.areas.find((a) => a.id === areaId);
        if (!area) return;
        area.imageSize = chosen as ImageSize;
        render();
        notifyContent();
        emit();
      });
      return;
    }

    if (action === 'extras-popover' && areaId) {
      openExtrasPopover(actionEl, areaId, render, emit);
      return;
    }

    if (action === 'target-add' && areaId) {
      const select = row!.querySelector<HTMLSelectElement>('[data-role="target-add-select"]');
      if (!select || !select.value) return;
      const area = state.areas.find((a) => a.id === areaId);
      if (!area) return;
      if (area.targets.some((t) => t.targetId === select.value)) return;
      area.targets.push({
        id: nextAreaTargetId(),
        targetId: select.value,
        description: '',
      });
      render();
      notifyContent();
      emit();
      return;
    }

    if (action === 'target-remove' && areaId) {
      const atId = actionEl.dataset.areaTargetId;
      if (!atId) return;
      const area = state.areas.find((a) => a.id === areaId);
      if (!area) return;
      area.targets = area.targets.filter((t) => t.id !== atId);
      render();
      notifyContent();
      emit();
      return;
    }
  };

  const onInput = (e: Event): void => {
    if (!(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLInputElement)) return;
    const role = e.target.dataset.role;
    const row = e.target.closest<HTMLElement>('.area-row');
    const areaId = row?.dataset.areaId;
    if (!areaId) return;
    const area = state.areas.find((a) => a.id === areaId);
    if (!area) return;

    if (role === 'target-description') {
      const atId = e.target.dataset.areaTargetId;
      const at = area.targets.find((t) => t.id === atId);
      if (!at) return;
      at.description = e.target.value;
      notifyContent();
      emit();
    }
  };

  // Row reorder DnD.
  const onDragStart = (e: DragEvent): void => {
    if (!(e.target instanceof HTMLElement)) return;
    const row = e.target.closest<HTMLElement>('.area-row');
    if (!row || !container.contains(row)) return;
    // Don't trigger drag from nested inputs/controls.
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      e.target instanceof HTMLSelectElement ||
      e.target instanceof HTMLButtonElement
    ) {
      e.preventDefault();
      return;
    }
    e.dataTransfer?.setData('text/plain', `area:${row.dataset.areaId}`);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    row.classList.add('is-dragging');
  };

  const onDragOver = (e: DragEvent): void => {
    if (!(e.target instanceof HTMLElement)) return;
    const row = e.target.closest<HTMLElement>('.area-row');
    if (!row || !container.contains(row)) return;
    if (!hasAreaDrag(e)) return;
    e.preventDefault();
    const rect = row.getBoundingClientRect();
    const above = e.clientY - rect.top < rect.height / 2;
    row.classList.toggle('drop-above', above);
    row.classList.toggle('drop-below', !above);
  };

  const onDragLeave = (e: DragEvent): void => {
    if (!(e.target instanceof HTMLElement)) return;
    const row = e.target.closest<HTMLElement>('.area-row');
    if (!row) return;
    row.classList.remove('drop-above', 'drop-below');
  };

  const onDrop = (e: DragEvent): void => {
    if (!(e.target instanceof HTMLElement)) return;
    const row = e.target.closest<HTMLElement>('.area-row');
    if (!row || !container.contains(row)) return;
    const data = e.dataTransfer?.getData('text/plain') ?? '';
    if (!data.startsWith('area:')) return;
    e.preventDefault();
    const fromId = data.slice('area:'.length);
    const toId = row.dataset.areaId ?? '';
    const above = row.classList.contains('drop-above');
    row.classList.remove('drop-above', 'drop-below');
    reorderAreas(fromId, toId, above);
    render();
    notifyStructure();
    emit();
  };

  const onDragEnd = (): void => {
    container.querySelectorAll('.area-row').forEach((r) => {
      r.classList.remove('is-dragging', 'drop-above', 'drop-below');
    });
  };

  container.addEventListener('click', onClick);
  container.addEventListener('input', onInput);
  container.addEventListener('dragstart', onDragStart);
  container.addEventListener('dragover', onDragOver);
  container.addEventListener('dragleave', onDragLeave);
  container.addEventListener('drop', onDrop);
  container.addEventListener('dragend', onDragEnd);

  subscribeStructure(render);
  render();
}

function renderCropRow(): string {
  const c = state.cropArea;
  const coords = c ? `${c.x}, ${c.y} · ${c.w}×${c.h}` : '—';
  const hidden = !!c?.hidden;
  const visibleIcon = hidden
    ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>'
    : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
  return `
    <div class="area-row crop-area-row${hidden ? ' is-hidden' : ''}">
      <div class="area-row-main">
        <button type="button" class="area-visible-btn ${hidden ? '' : 'is-active'}" data-action="toggle-crop-hidden" title="${hidden ? 'Show crop rect' : 'Hide crop rect'}">${visibleIcon}</button>
        <span class="area-index crop-area-index">Crop</span>
        <span class="area-coords" title="Crop position · dimensions">${coords}</span>
        <span class="crop-area-hint">Used for label compositing</span>
      </div>
    </div>
  `;
}

function renderAreaRow(area: Area, idx: number, expanded: boolean): string {
  const dims = areaDims(area);
  const assigned = new Set(area.targets.map((t) => t.targetId));
  const available = state.targets.filter((t) => !assigned.has(t.id));

  const targetsHtml = area.targets.length === 0
    ? `<p class="area-targets-empty">No targets assigned.</p>`
    : area.targets
        .map((at) => renderAreaTargetRow(at, targetNeedsDescription(at.targetId)))
        .join('');

  const addSelectHtml = available.length === 0
    ? `<span class="area-target-add-empty">All targets assigned</span>`
    : `
        <select class="area-target-add-select" data-role="target-add-select">
          <option value="" disabled selected>Add target…</option>
          ${available.map((t) => `<option value="${escapeHtml(t.id)}">${escapeHtml(t.name)}</option>`).join('')}
        </select>
        <button type="button" class="area-target-add-btn" data-action="target-add">+ Add</button>
      `;

  const photoOn = area.includePhoto ? 'is-active' : '';
  const baseOn = area.includeBase ? 'is-active' : '';
  const extrasCount = area.extraImages.length;
  const extrasOn = extrasCount > 0 ? 'is-active' : '';
  const visibleIcon = area.hidden
    ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>'
    : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';

  return `
    <div class="area-row${expanded ? ' expanded' : ''}${area.hidden ? ' is-hidden' : ''}" data-area-id="${area.id}" draggable="true">
      <div class="area-row-main">
        <button type="button" class="area-handle" title="Drag to reorder" tabindex="-1">⋮⋮</button>
        <button type="button" class="area-visible-btn ${area.hidden ? '' : 'is-active'}" data-action="toggle-hidden" title="${area.hidden ? 'Show area rect' : 'Hide area rect'}">${visibleIcon}</button>
        <span class="area-index" style="color:${areaColor(idx)}">#${idx + 1}</span>
        <button type="button" class="area-pill area-aspect-btn" data-action="aspect-popover" title="Aspect ratio">${escapeHtml(area.aspectRatio)}</button>
        <button type="button" class="area-pill area-size-btn" data-action="size-popover" title="Image size">${escapeHtml(area.imageSize)}</button>
        <span class="area-coords" title="Position · dimensions">${area.x}, ${area.y} · ${dims.w}×${dims.h}</span>
        <button type="button" class="area-base-btn ${baseOn}" data-action="toggle-base" title="Include full base image as reference">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V3h4"/><path d="M17 3h4v4"/><path d="M21 17v4h-4"/><path d="M7 21H3v-4"/></svg>
        </button>
        <button type="button" class="area-extras-btn ${extrasOn}" data-action="extras-popover" title="Extra images">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          ${extrasCount > 0 ? `<span class="area-extras-count">${extrasCount}</span>` : ''}
        </button>
        <button type="button" class="area-photo-btn ${photoOn}" data-action="toggle-photo" title="Include photo input">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </button>
        <button type="button" class="area-delete-btn" data-action="delete" title="Delete area">×</button>
        <button type="button" class="area-expand-btn" data-action="toggle-expand" title="Expand/collapse">▾</button>
      </div>
      <div class="area-row-body">
        <div class="area-targets-list">${targetsHtml}</div>
        <div class="area-target-add-row">${addSelectHtml}</div>
      </div>
    </div>
  `;
}

function renderAreaTargetRow(at: AreaTarget, needsDescription: boolean): string {
  const target = state.targets.find((t) => t.id === at.targetId);
  const name = target ? target.name : `(missing: ${at.targetId})`;
  const descriptionHtml = needsDescription
    ? `<textarea
        class="area-target-description"
        data-role="target-description"
        data-area-target-id="${at.id}"
        placeholder="[DESCRIPTION]"
        rows="1"
      >${escapeHtml(at.description)}</textarea>`
    : '';
  return `
    <div class="area-target-row${needsDescription ? '' : ' no-description'}" data-area-target-id="${at.id}">
      <span class="area-target-name">${escapeHtml(name)}</span>
      ${descriptionHtml}
      <button type="button" class="area-target-remove-btn" data-action="target-remove" data-area-target-id="${at.id}" title="Remove">×</button>
    </div>
  `;
}

function targetNeedsDescription(targetId: string): boolean {
  const target = state.targets.find((t) => t.id === targetId);
  if (!target) return false;
  for (const q of state.questions) {
    if (isChoice(q)) {
      for (const opt of q.options) {
        for (const p of opt.prompts) {
          if (p.alternatives.some((a) => a.target === target.name)) return true;
        }
      }
    } else if (isPhoto(q)) {
      for (const p of q.prompts) {
        if (p.alternatives.some((a) => a.target === target.name)) return true;
      }
    }
  }
  return false;
}

function aspectPopoverHtml(_areaId: string): string {
  return ASPECT_RATIOS.map(
    (a) =>
      `<button type="button" class="area-popover-option" data-popover-value="${escapeHtml(a.ratio)}">${escapeHtml(a.ratio)} <span class="area-popover-dim">${a.w}×${a.h}</span></button>`,
  ).join('');
}

function sizePopoverHtml(_areaId: string): string {
  return IMAGE_SIZES.map(
    (s) =>
      `<button type="button" class="area-popover-option" data-popover-value="${escapeHtml(s.key)}">${escapeHtml(s.key)} <span class="area-popover-dim">×${s.factor}</span></button>`,
  ).join('');
}

let activePopover: HTMLElement | null = null;
function closeActivePopover(): void {
  if (!activePopover) return;
  activePopover.remove();
  activePopover = null;
}

const POPOVER_MARGIN = 8;

function positionPopover(pop: HTMLElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  const popRect = pop.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  let left = rect.left;
  const maxLeft = vw - popRect.width - POPOVER_MARGIN;
  if (left > maxLeft) left = Math.max(POPOVER_MARGIN, maxLeft);
  if (left < POPOVER_MARGIN) left = POPOVER_MARGIN;

  let top = rect.bottom + 4;
  if (top + popRect.height > vh - POPOVER_MARGIN) {
    const above = rect.top - 4 - popRect.height;
    top = above >= POPOVER_MARGIN
      ? above
      : Math.max(POPOVER_MARGIN, vh - popRect.height - POPOVER_MARGIN);
  }

  pop.style.top = `${top}px`;
  pop.style.left = `${left}px`;
}

function togglePopover(
  anchor: HTMLElement,
  contentHtml: () => string,
  onPick: (value: string) => void,
): void {
  if (activePopover && activePopover.dataset.anchorFor === anchor.dataset.action) {
    closeActivePopover();
    return;
  }
  closeActivePopover();
  const pop = document.createElement('div');
  pop.className = 'area-popover';
  pop.dataset.anchorFor = anchor.dataset.action ?? '';
  pop.innerHTML = contentHtml();
  document.body.appendChild(pop);
  positionPopover(pop, anchor);
  activePopover = pop;

  const onPopClick = (ev: Event): void => {
    if (!(ev.target instanceof Element)) return;
    const opt = ev.target.closest<HTMLElement>('[data-popover-value]');
    if (!opt) return;
    const value = opt.dataset.popoverValue!;
    closeActivePopover();
    onPick(value);
  };
  pop.addEventListener('click', onPopClick);

  const onDocClick = (ev: Event): void => {
    if (!(ev.target instanceof Node)) return;
    if (pop.contains(ev.target) || anchor.contains(ev.target)) return;
    closeActivePopover();
    document.removeEventListener('mousedown', onDocClick, true);
  };
  setTimeout(() => document.addEventListener('mousedown', onDocClick, true), 0);
}

function openExtrasPopover(
  anchor: HTMLElement,
  areaId: string,
  render: () => void,
  emit: () => void,
): void {
  if (activePopover && activePopover.dataset.anchorFor === 'extras-popover' && activePopover.dataset.areaId === areaId) {
    closeActivePopover();
    return;
  }
  closeActivePopover();

  const pop = document.createElement('div');
  pop.className = 'area-popover area-extras-popover';
  pop.dataset.anchorFor = 'extras-popover';
  pop.dataset.areaId = areaId;
  document.body.appendChild(pop);
  activePopover = pop;

  const rerenderPopover = (): void => {
    const area = state.areas.find((a) => a.id === areaId);
    if (!area) {
      closeActivePopover();
      return;
    }
    const itemsHtml = area.extraImages.length === 0
      ? `<p class="area-extras-empty">No extra images yet.</p>`
      : area.extraImages
          .map(
            (ei, idx) => `
              <div class="area-extras-item" data-extra-id="${ei.id}">
                <span class="area-extras-idx">${idx + 1}</span>
                <img class="area-extras-thumb" src="${ei.dataUrl}" alt="" />
                <span class="area-extras-name" title="${escapeHtml(ei.name)}">${escapeHtml(ei.name)}</span>
                <button type="button" class="area-extras-remove" data-extras-remove="${ei.id}" title="Remove">×</button>
              </div>
            `,
          )
          .join('');

    pop.innerHTML = `
      <div class="area-extras-header">Extra images (inserted between crop and photo)</div>
      <div class="area-extras-list">${itemsHtml}</div>
      <label class="area-extras-dropzone">
        <input type="file" accept="image/*" multiple hidden data-extras-file-input />
        <span class="area-extras-dropzone-label">Drop images here or click to browse</span>
      </label>
    `;
    positionPopover(pop, anchor);
  };

  const addFiles = async (files: FileList | null | undefined): Promise<void> => {
    if (!files || files.length === 0) return;
    const area = state.areas.find((a) => a.id === areaId);
    if (!area) return;
    const additions: AreaImage[] = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      const dataUrl = await fileToDataUrl(file);
      additions.push({
        id: nextAreaImageId(),
        name: file.name,
        dataUrl,
        mimeType: file.type,
      });
    }
    if (additions.length === 0) return;
    area.extraImages.push(...additions);
    rerenderPopover();
    render();
    notifyContent();
    emit();
  };

  pop.addEventListener('click', (ev: Event) => {
    if (!(ev.target instanceof Element)) return;
    const removeBtn = ev.target.closest<HTMLElement>('[data-extras-remove]');
    if (!removeBtn) return;
    const id = removeBtn.dataset.extrasRemove!;
    const area = state.areas.find((a) => a.id === areaId);
    if (!area) return;
    area.extraImages = area.extraImages.filter((ei) => ei.id !== id);
    rerenderPopover();
    render();
    notifyContent();
    emit();
  });

  pop.addEventListener('change', (ev: Event) => {
    if (!(ev.target instanceof HTMLInputElement)) return;
    if (ev.target.dataset.extrasFileInput === undefined) return;
    const input = ev.target;
    void addFiles(input.files).then(() => {
      input.value = '';
    });
  });

  pop.addEventListener('dragenter', (ev: DragEvent) => {
    if (!(ev.target instanceof Element)) return;
    const dz = ev.target.closest<HTMLElement>('.area-extras-dropzone');
    if (!dz) return;
    ev.preventDefault();
    dz.classList.add('dragging');
  });

  pop.addEventListener('dragover', (ev: DragEvent) => {
    if (!(ev.target instanceof Element)) return;
    const dz = ev.target.closest<HTMLElement>('.area-extras-dropzone');
    if (!dz) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    dz.classList.add('dragging');
  });

  pop.addEventListener('dragleave', (ev: DragEvent) => {
    if (!(ev.target instanceof Element)) return;
    const dz = ev.target.closest<HTMLElement>('.area-extras-dropzone');
    if (!dz) return;
    const related = ev.relatedTarget;
    if (related instanceof Node && dz.contains(related)) return;
    dz.classList.remove('dragging');
  });

  pop.addEventListener('drop', (ev: DragEvent) => {
    if (!(ev.target instanceof Element)) return;
    const dz = ev.target.closest<HTMLElement>('.area-extras-dropzone');
    if (!dz) return;
    ev.preventDefault();
    dz.classList.remove('dragging');
    void addFiles(ev.dataTransfer?.files ?? null);
  });

  rerenderPopover();

  const onDocClick = (ev: Event): void => {
    if (!(ev.target instanceof Node)) return;
    if (pop.contains(ev.target) || anchor.contains(ev.target)) return;
    closeActivePopover();
    document.removeEventListener('mousedown', onDocClick, true);
  };
  setTimeout(() => document.addEventListener('mousedown', onDocClick, true), 0);
}

function hasAreaDrag(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('text/plain');
}

function reorderAreas(fromId: string, toId: string, above: boolean): void {
  if (fromId === toId) return;
  const fromIdx = state.areas.findIndex((a) => a.id === fromId);
  if (fromIdx < 0) return;
  const [moving] = state.areas.splice(fromIdx, 1);
  let toIdx = state.areas.findIndex((a) => a.id === toId);
  if (toIdx < 0) {
    state.areas.splice(fromIdx, 0, moving);
    return;
  }
  if (!above) toIdx += 1;
  state.areas.splice(toIdx, 0, moving);
}
